import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usuariosService, rolesService, permissoesService } from '../services/acesso.service'
import type { UsuarioFilters, RoleFilters } from '../types'

// ── Usuarios ────────────────────────────────────────────────

export const USUARIOS_KEY = 'usuarios'

export function useUsuarios(filters: UsuarioFilters = {}) {
  return useQuery({
    queryKey: [USUARIOS_KEY, filters],
    queryFn: () => usuariosService.list(filters),
  })
}

export function useCreateUsuario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: usuariosService.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [USUARIOS_KEY] }),
  })
}

export function useUpdateUsuario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => usuariosService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [USUARIOS_KEY] }),
  })
}

export function useDeleteUsuario() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => usuariosService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [USUARIOS_KEY] }),
  })
}

// ── Roles ────────────────────────────────────────────────────

export const ROLES_KEY = 'roles'

export function useRoles(filters: RoleFilters = {}) {
  return useQuery({
    queryKey: [ROLES_KEY, filters],
    queryFn: () => rolesService.list(filters),
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: rolesService.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [ROLES_KEY] }),
  })
}

export function useUpdateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => rolesService.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ROLES_KEY] }),
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => rolesService.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ROLES_KEY] }),
  })
}

// ── Permissoes ───────────────────────────────────────────────

export const PERMISSOES_KEY = 'permissoes'

export function usePermissoes() {
  return useQuery({
    queryKey: [PERMISSOES_KEY],
    queryFn: () => permissoesService.list(),
    staleTime: 5 * 60 * 1000,
  })
}
